/*
 * TTTech nerve-management-system-frontend
 * Copyright(c) 2026 TTTech Industrial Automation AG.
 *
 * ALL RIGHTS RESERVED.
 * Usage of this software, including source code, netlists, documentation,
 * is subject to restrictions and conditions of the applicable license
 * agreement with TTTech Industrial Automation AG or its affiliates.
 *
 * All trademarks used are the property of their respective owners.
 * TTTech Industrial Automation AG and its affiliates do not assume any liability
 * arising out of the application or use of any product described or shown
 * herein. TTTech Industrial Automation AG and its affiliates reserve the right to
 * make changes, at any time, in order to improve reliability, function or
 * design.
 *
 * Contact Information:
 * support@tttech-industrial.com
 * TTTech Industrial Automation AG, Schoenbrunnerstrasse 7, 1040 Vienna, Austria
 */
import { defineStore } from 'pinia';
import AuthModel from '@/model/auth/auth.model';
import { AuthApiService, PermissionApiService } from '@/services/api';
import PasswordPolicyModel from '@/model/password-policy/password-policy.model';

const useAuthStore = defineStore('auth', {
  state: () => ({
    user: {},
    userPermissions: {},
    serviceUnavailable: {},
    userMFA: {},
    passwordPolicy: {},
  }),

  getters: {
    isLoggedIn: (state) => () => !!state.user?.username,
  },

  actions: {
    // Logic Actions
    async login(authData) {
      const response = await AuthApiService.login(authData);

      if (response.user) {
        this.userPermissions = response.user.permissions;
        this.user = new AuthModel(response.user);

        localStorage.setItem('userDetails', JSON.stringify(response.user));
        localStorage.setItem(
          'session',
          JSON.stringify({ userId: response.user._id, sessionId: response.user.sessionId }),
        );
      } else if (response.mfa) {
        this.userMFA = response.mfa || {};
      }

      await this.checkUserLogged();
      return response;
    },

    async validateMFA(params) {
      const response = await AuthApiService.validateMFA(params);
      this.userPermissions = response.user.permissions;
      this.user = new AuthModel(response.user);

      localStorage.setItem('userDetails', JSON.stringify(response.user));
      localStorage.setItem(
        'session',
        JSON.stringify({ userId: response.user._id, sessionId: response.user.sessionId }),
      );
    },

    async removeMFA() {
      this.userMFA = {};
    },

    async checkUserLogged() {
      try {
        const loggedUser = JSON.parse(localStorage.getItem('userDetails'));
        const userId = loggedUser ? loggedUser._id : '';

        if (userId) {
          this.user = new AuthModel(loggedUser);
          const response = await PermissionApiService.getPermissionByUserId(userId);
          this.userPermissions = response;
          this.userMFA = {};
          return;
        }
        await this.clearLocalStorage();
      } catch (e) {
        await this.clearLocalStorage();
        throw e;
      }
    },

    async clearLocalStorage() {
      localStorage.removeItem('userDetails');
      localStorage.removeItem('session');
      this.user = null;
    },

    clearAuthState() {
      this.$patch({
        user: null,
        userPermissions: {},
        userMFA: {},
        serviceUnavailable: {},
      });
      localStorage.removeItem('userDetails');
      localStorage.removeItem('session');
    },

    setServiceUnavailable(obj) {
      this.serviceUnavailable = obj;
    },

    async updateUser(user) {
      this.user = new AuthModel(user);
    },

    async getPasswordPolicy() {
      const response = await AuthApiService.getPasswordPolicy();
      this.passwordPolicy = new PasswordPolicyModel(response);
    },

    updateLocalStorage(userData) {
      let userDetails = JSON.parse(localStorage.getItem('userDetails'));
      userDetails = { ...userDetails, ...userData };
      localStorage.setItem('userDetails', JSON.stringify(userDetails));
      this.updateUser(JSON.parse(localStorage.getItem('userDetails')));
    },

    /**
     * @description Event coming from auth and crm user services. Handles:
     * - logoutSpecificUser: logs out the user from all sessions if triggered for the logged in user
     * - roleUpdated: notifies the logged in user that their role was changed by an admin
     * @param {object} payload - mqtt message payload containing event name and userId
     * @returns {object} - object containing logout/roleUpdated status
     */
    handleAuthAndCrmUserEvents(payload) {
      const name = payload?.msg || payload?.name;
      const userId = payload?.userId || payload?.params?.userId;
      const isForLoggedInUser = userId === this.user.id;

      if (name === 'logoutSpecificUser' && isForLoggedInUser) {
        return { logout: true, roleUpdated: false };
      }
      if (name === 'roleUpdated' && isForLoggedInUser) {
        return { logout: false, roleUpdated: true };
      }
      return { logout: false, roleUpdated: false };
    },
  },
});

export default useAuthStore;
